import { BlockViewModel, PageViewModel, DocumentViewModel } from '../../..';
import { DocumentLayout } from '..';
import { SectionValuesUpdateType } from '../../LazyLoaderManager';
import { Logger } from '_common/services';
import { PageElement } from 'Editor/services/VisualizerManager/Views';
import { EditorDOMElements, EditorDOMUtils } from 'Editor/services/_Common/DOM';

type PagePosition = 'LEFT' | 'RIGHT';

type PageSide = 'VERSO' | 'RECTO';

class RenderStatus {
  protected stopped: boolean = false;
  protected canceled: boolean = false;
  protected rendering: boolean = false;
  processing?: Promise<any>;
  index: number = 0;

  get isRendering() {
    return this.rendering;
  }

  start(index: number) {
    this.stopped = false;
    this.canceled = false;
    this.rendering = true;
    this.index = index;
  }

  stop() {
    this.stopped = true;
    this.canceled = false;
    this.rendering = false;
    this.index = 0;
  }
}

class DeferRequest {
  promise?: Promise<any>;
  resolve?: (value: any) => void;
  reject?: () => void;

  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
    Object.freeze(this);
  }
}

class RenderTask<E extends () => void = () => void> {
  task: E;
  args: Parameters<E>;
  deferred: DeferRequest;

  constructor(task: E, ...args: Parameters<E>) {
    this.task = task;
    this.args = args;
    this.deferred = new DeferRequest();
  }

  // get promise() {
  //   return this.deferred.promise;
  // }

  async exec() {
    await this.task.apply(null, this.args);
  }
}

class RenderQueue {
  protected queue: RenderTask[] = [];

  // running: DeferRequest | null = null;
  // paused: boolean = false;
  protected running: boolean = false;
  protected runPromise: Promise<void> | undefined;

  protected afterRunCallback: (() => Promise<void> | void) | undefined;

  constructor(afterRunCallback?: () => Promise<void> | void) {
    this.afterRunCallback = afterRunCallback;
  }

  enqueue<E extends (...args: any) => void>(task: E, ...args: Parameters<E>) {
    if (!task) {
      throw new Error('No Task Provided');
    }
    const newTask = new RenderTask(task, ...args);
    this.queue.push(newTask);

    // Wait to start queue flush
    if (!this.running) {
      this.run();
    }

    return this.runPromise;
  }

  clear() {
    this.queue = [];
    // if (this.running) {
    //   this.running.resolve && this.running.resolve(null);
    //   this.running = null;
    // }
  }

  stop() {
    // this.queue = [];
    // if (this.running) {
    //   this.running.resolve && this.running.resolve(null);
    //   this.running = null;
    // }
    // this.paused = true;
  }

  // private dequeue() {
  //   let inwait: any;
  //   let task;
  //   let args;
  //   let result;

  //   if (this.queue.length && !this.paused) {
  //     inwait = this.queue.shift();
  //     task = inwait.task;
  //     args = inwait.args;
  //     if (task) {
  //       // console.log('args', args);

  //       result = task(...args);

  //       if (result && typeof result['then'] === 'function') {
  //         // Task is a function that returns a promise
  //         return result.then(
  //           () => {
  //             // logger.debug('TASK COMPLETED :');
  //             inwait.deferred.resolve.apply();
  //           },
  //           (error: Error) => {
  //             inwait.deferred.reject.apply(error);
  //           },
  //         );
  //       } else {
  //         // Task resolves immediately
  //         inwait.deferred.resolve.apply();
  //         return inwait.promise;
  //       }
  //     }
  //   } else {
  //     return Promise.resolve();
  //   }
  // }

  private run() {
    if (this.running) {
      return this.runPromise;
    }

    this.runPromise = new Promise<void>(async (resolve, reject) => {
      this.running = true;
      requestAnimationFrame(async () => {
        try {
          while (this.queue.length) {
            let task = this.queue.shift();
            if (task) {
              await task.exec();
            }
          }

          if (this.afterRunCallback) {
            await this.afterRunCallback();
          }

          this.running = false;
          resolve();
        } catch (error) {
          this.running = false;
          reject(error);
        }
      });
    });

    // requestAnimationFrame(() => {
    //   if (this.queue.length) {
    //     this.dequeue().then(this.run.bind(this));
    //   } else {
    //     this.running?.resolve && this.running?.resolve(null);
    //     this.running = null;
    //   }
    // });

    // // Unpause
    // if (this.paused === true) {
    //   this.paused = false;
    // }

    // return this.running.promise;
  }
}

export class PageLayout extends DocumentLayout {
  protected pages: PageViewModel[];
  protected scheduledRefresh: any = {};
  protected renderstatus = new RenderStatus();
  protected queue: RenderQueue;

  private debug: boolean = true;

  private refreshTimeout: NodeJS.Timeout | null = null;

  private reflowPageIndex: number | undefined;

  private timeouts: { [key: string]: NodeJS.Timeout } = {};

  constructor(
    Data: Editor.Data.API,
    Visualizer: Partial<Editor.Visualizer.State>,
    vm: DocumentViewModel,
  ) {
    super(Data, Visualizer, vm);
    this.pages = [];
    this.insertViewModelView = this.insertViewModelView.bind(this);
    this.removeViewModelView = this.removeViewModelView.bind(this);
    this.reflowPages = this.reflowPages.bind(this);

    this.queue = new RenderQueue();
  }

  destroy(): void {
    this.removeAllChildren();
  }

  removeAllChildren() {
    while (this.pages.length) {
      this.pages.pop()?.dispose();
    }
    while (this.vm.view?.firstChild) {
      this.vm.view?.removeChild(this.vm.view?.firstChild);
    }
  }

  handleChildChangedHeight(
    viewModel: BlockViewModel,
    view?: Editor.Visualizer.BaseView | null,
    difference?: number,
    scrollDiff?: number,
  ) {
    logger.trace('[PageLayout] handleChildChangedHeight', viewModel);
    // const pageElement = EditorDOMUtils.closest(
    //   viewModel.getRootView() || null,
    //   'PAGE-ELEMENT',
    //   this.vm.view,
    // ) as PageElement;

    // if (pageElement) {
    // const index = this.vm.children.indexOf(viewModel);

    if (view) {
      const pageVM = this.getPageFromView(view);

      if (!pageVM) {
        return;
      }

      const pageIndex = this.pages.indexOf(pageVM);
      const nextPage = this.pages[pageIndex + 1];

      const isSplitedView = EditorDOMElements.isSupportedBlockElement(view)
        ? view.isSplittedView()
        : false;

      if (pageVM.hasOverflow() || pageVM.hasUnderflow(nextPage) || isSplitedView) {
        logger.info('[PageLayout] hasOverflow hasUnderflow');
        this.reflowPages(pageIndex);
        //   // this.scheduleRefreshRender(index);
        // } else if (viewModel.hasSplitViews()) {
        //   // TODO:
        //   // TEMP: for now force rerender splited views, in the future check if the respective view as space for more text
        //   // this.scheduleRefreshRender(index);
        // }
      }
    }

    // TODO: handle scroll (do we need to handle scroll here?)
  }

  private async removeBlocksViews(startIndex: number) {
    let viewModel;

    // remove blocks
    for (let index = this.vm?.children.length - 1; index >= startIndex; index--) {
      viewModel = this.vm?.children.getAtIndex(index) as BlockViewModel;
      await this.removeViewModelView(viewModel, index, false);
    }

    // remove pages
    const workingPage = this.getPageFromChildIndex(startIndex);
    if (workingPage) {
      let pageIndex = this.pages.indexOf(workingPage);
      if (pageIndex >= 0) {
        while (this.pages.length > pageIndex) {
          this.removePage(pageIndex);
        }
      }
    }
  }

  async updateBlockSection(viewModel: BlockViewModel) {
    const blockIndex = this.vm.children.indexOf(viewModel);
    if (blockIndex >= 0) {
      await this.scheduleRefreshRender(blockIndex);
    }
  }

  updateSectionsValues(delta: SectionValuesUpdateType) {
    const blocksToUpdate = Object.keys(delta);
    let blockId;
    let block: BlockViewModel;
    let blockIndex: number;
    for (let index = 0; index < blocksToUpdate.length; index++) {
      blockId = blocksToUpdate[index];
      block = this.vm.children.getById(blockId) as BlockViewModel;
      if (block && block.loaded) {
        block.updateSectionValue(delta[blockId]);
        blockIndex = this.vm.children.indexOf(block);
        if (blockIndex >= 0) {
          this.scheduleRefreshRender(blockIndex);
        }
      }
    }
  }

  updateSectionProperties(sectionId: string) {
    let block = this.vm.view?.querySelector(
      `section-element[section="${sectionId}"] > *:first-child`,
    ) as Editor.Elements.BaseViewElement;
    if (block && block.vm) {
      let blockIndex = this.vm.children.indexOf(block.vm);
      this.scheduleRefreshRender(blockIndex);
    }
  }

  handleParagraphStyleUpdate() {
    this.scheduleReflowPages(0);
  }

  async appendChild(viewModel: BlockViewModel) {
    if (this.vm.view) {
      // viewModel.setParent(this.vm);
      // this.vm.children.push(viewModel);
      if (!viewModel.loaded) {
        await viewModel.awaitForEvent('LOADED');
      }

      // this.renderstatus.start(this.vm.children.length - 1);
      // this.renderstatus.processing = this.layoutViewModelView(viewModel);
      await this.insertViewModelView(viewModel);

      // await this.renderstatus.processing;
      // this.queue.enqueue(this.layoutViewModelView, viewModel);

      // this.renderstatus.stop();
    }
  }

  async insertChildAt(viewModel: BlockViewModel, index: number) {
    if (!viewModel.loaded) {
      await viewModel.awaitForEvent('LOADED');
    }

    // insert child in page
    await this.insertViewModelView(viewModel, index);
  }

  async replaceChildAt(newViewModel: BlockViewModel, oldViewModel: BlockViewModel, index: number) {
    if (!newViewModel.loaded) {
      await newViewModel.awaitForEvent('LOADED');
    }

    // await this.queue.enqueue(this.layoutViewModelView, newViewModel, index);
    await this.insertViewModelView(newViewModel, index, false);

    await this.removeViewModelView(oldViewModel, index, true);
  }

  async removeChildAt(viewModel: BlockViewModel, index: number) {
    // remove child from page
    await this.removeViewModelView(viewModel, index);
  }
  // ################################################################
  // #region Page Utilities
  // ################################################################
  get numPages() {
    return this.pages.length;
  }

  getPageAtIndex(index: number) {
    return this.pages[index];
  }

  pagePosition(index: number): PagePosition {
    return index % 2 === 0 ? 'LEFT' : 'RIGHT';
  }

  pageSide(index: number): PageSide {
    return index % 2 === 0 ? 'VERSO' : 'RECTO';
  }

  private updatePagesNumbering() {
    for (let index = 0; index < this.pages.length; index++) {
      this.pages[index].setPageNumber(index + 1);
    }
  }

  private insertNewPageAfter(pageIndex: number, sectionId?: string) {
    const page = this.Visualizer.viewModelFactory?.getPage(sectionId);

    if (page) {
      let pageView = page.getRootView();

      if (!pageView) {
        throw new Error('No view found for the new page');
      }

      if (pageIndex >= this.pages.length - 1) {
        this.pages.push(page);
        page.setPageNumber(this.pages.length);

        this.vm.view?.appendChild(pageView);
      } else {
        const nextPageView =
          pageIndex + 1 < this.pages.length ? this.pages[pageIndex + 1].getRootView() : null;

        if (!nextPageView) {
          throw new Error('No view found for the next page');
        }

        this.vm.view?.insertBefore(pageView, nextPageView);

        this.pages.splice(pageIndex + 1, 0, page);
        this.updatePagesNumbering();
      }

      return page;
    } else {
      throw new Error('Can not create a new page');
    }
  }

  private appendNewPage(sectionId?: string) {
    const page = this.Visualizer.viewModelFactory?.getPage(sectionId);
    if (page) {
      this.pages.push(page);
      page.setPageNumber(this.pages.length);

      let pageView = page.getRootView() as PageElement;

      if (pageView) {
        this.vm.view?.appendChild(pageView);
      }

      return page;
    } else {
      throw new Error('Can not create a new page');
    }
  }

  private removePage(pgIndex: number) {
    const pageView = this.pages[pgIndex].getRootView();
    pageView?.remove();
    this.pages.splice(pgIndex, 1);
  }

  private getPageFromChildIndex(index: number) {
    let blockViewModel = this.vm?.children.getAtIndex(index);
    if (blockViewModel) {
      let view = blockViewModel.getRootView();
      if (view) {
        return this.getPageFromView(view);
      }
    }
    return null;
  }

  private getPageFromView(view: Editor.Visualizer.BaseView) {
    let pageView = EditorDOMUtils.closest(view || null, 'PAGE-ELEMENT');
    if (EditorDOMElements.isPageElement(pageView) && pageView.vm) {
      return pageView.vm;
    }

    return null;
  }

  getLastPage(sectionId?: string) {
    if (this.pages.length === 0) {
      this.appendNewPage(sectionId);
    }
    return this.pages[this.pages.length - 1];
  }

  private getNextPage(pgIndex: number, insertNewIfNotExist: boolean = false) {
    let nextPageIndex = pgIndex + 1;
    if (nextPageIndex >= this.pages.length && insertNewIfNotExist) {
      return this.appendNewPage();
    }
    return this.pages[nextPageIndex];
  }

  // ################################################################
  // #region Content Rendering
  // ################################################################

  /**
   * Append the view of a view model to the page
   */
  private async insertViewModelView(
    viewModel: BlockViewModel,
    indexToInsert?: number,
    reflowPages: boolean = true,
  ) {
    this.renderstatus.index = this.vm.children.indexOf(viewModel);

    let rootView = viewModel.render();

    if (!rootView) {
      return;
    }

    // let contents: Editor.Visualizer.BaseView[] = [rootView];
    let toInsert: Editor.Visualizer.BaseView | undefined;

    let sectionId = this.Data.sections.getSectionOfBlock(viewModel.id);

    let referenceView: Editor.Visualizer.BaseView | undefined;

    let workingPage: PageViewModel | null = null;
    if (indexToInsert != null && indexToInsert < this.vm.children.length) {
      workingPage = this.getPageFromChildIndex(indexToInsert);
      referenceView = this.vm?.children.getAtIndex(indexToInsert)?.getRootView();
    } else {
      workingPage = this.getLastPage(sectionId);
    }

    if (!workingPage) {
      throw new Error('No page found to insert the content');
    }

    // while (contents.length > 0 /* && this.renderstatus.isRendering */) {
    // toInsert = contents.shift();
    toInsert = rootView;

    //   if (!toInsert) {
    //     continue;
    //   }

    // insert Page after the current page based on the index to insert
    let insertIntoNextPage: boolean = false;

    // pre process
    if (EditorDOMElements.isParagraphElement(toInsert)) {
      insertIntoNextPage =
        toInsert.getPageBreakBefore() === true ||
        (toInsert.styleId != null
          ? this.Data.styles.getStylePageBreakBefore(toInsert.styleId) === true
          : false);
    }

    if (insertIntoNextPage) {
      // TODO: should this insert a new page? or just insert the content into the next page
      workingPage = this.insertNewPageAfter(this.pages.indexOf(workingPage), sectionId);
      referenceView = undefined; // reset reference view as we are inserting a new page
    }

    // TODO: layout content based on the reference element
    // but if it is a new page, just append to that page

    // requestAnimationFrame(() => {
    await workingPage.insertContents(toInsert, sectionId, viewModel, referenceView);
    // this.scheduleReflowPages(this.pages.indexOf(workingPage));
    // });

    // TODO: check for section breaks;
    // const sectionBreak = toInsert.querySelector('section-break-element');
    // if (EditorDOMElements.isSectionBreakElement(sectionBreak)) {
    //   sectionId = sectionBreak.nextSection;
    // }

    // TODO: reflow pages

    // this.scheduleReflowPages(this.pages.indexOf(workingPage));
    if (reflowPages) {
      await this.reflowPages(this.pages.indexOf(workingPage));
    }

    //  let result = await workingPage.checkOverflow(toInsert, avoidPaginationProperties);

    // // TODO: overflow process should be check here
    // // and create reflow for the next pages if the working page is not the last one

    // if (result.success) {
    //   if (referenceView && !workingPage.containsView(referenceView)) {
    //     referenceView = undefined; // reset reference view if it is not in the current page anymore
    //   }

    //   if (result.shouldBreak) {
    //     if (result.breakToken && result.breakToken.isSectionBreak()) {
    //       sectionId = result.breakToken.getNextSection();
    //       referenceView = undefined; // reset reference view as we are inserting a new page
    //     }

    //     if (result.overflow || result.breakToken?.needsBreak()) {
    //       workingPage = this.insertNewPageAfter(this.pages.indexOf(workingPage), sectionId);
    //       referenceView = undefined; // reset reference view as we are inserting a new page
    //     }

    //     if (result.overflow) {
    //       contents.unshift(...(result.overflow || []));
    //       avoidPaginationProperties = true;
    //     }
    //   }
    // } else if (result.error) {
    //   throw result.error;
    // }
    // }
  }

  scheduleReflowPages(startPageIndex: number | undefined) {
    logger.trace('[PageLayout] scheduleReflowPages', startPageIndex);
    if (
      startPageIndex != null &&
      (this.reflowPageIndex == null || startPageIndex < this.reflowPageIndex)
    ) {
      this.reflowPageIndex = startPageIndex;
    }

    if (this.timeouts['reflowPages']) {
      clearTimeout(this.timeouts['reflowPages']);
    }

    this.timeouts['reflowPages'] = setTimeout(() => {
      requestAnimationFrame(() => {
        this.reflowPages(this.reflowPageIndex);
        this.reflowPageIndex = undefined;
      });
    }, 50);
  }

  async reflowPages(startPageIndex: number = 0) {
    logger.trace(`[PageLayout] reflowPages from ${startPageIndex}`);
    // TODO: if overflow or page break, move content to next page
    // else if underflow, try to move content from next page to current page
    // WARN: watch for splited views

    // TODO: find break points
    //  - paragraph properties (page break before)
    //  - page break
    //  - section break
    //  - content overflow

    let avoidPaginationProperties = false;

    let pageIndex = startPageIndex ?? 0;

    while (pageIndex < this.pages.length) {
      let workingPage = this.pages[pageIndex];

      // update page number
      workingPage.setPageNumber(pageIndex + 1);

      let pageContentContainer = workingPage.getRootView()?.contentContainer;

      if (!pageContentContainer) {
        throw new Error('No view found for the page during reflow');
      }

      const hasBreakPoints = workingPage.hasBreakElement(pageContentContainer);

      // workingPage.resetSplittedViews();

      let nextPage = this.getNextPage(pageIndex, false);

      let lastChild = workingPage.getLastBlockView();
      let lastChildViewModel = lastChild?.viewModel;

      // check for splitted views and reset them
      if (
        lastChildViewModel &&
        lastChild?.isSplittedView?.() &&
        nextPage &&
        (workingPage.hasOverflow() || workingPage.hasUnderflow(nextPage))
      ) {
        const numberOfSplitViews = lastChildViewModel?.splitViews.length || 0;
        const indexOfSplitView = lastChildViewModel?.indexOfSplitView(lastChild) ?? -1;
        const hasValidSplitIndex = indexOfSplitView >= 0 && numberOfSplitViews > 1;

        if (!hasValidSplitIndex) {
          logger.warn(
            `[PageLayout] skip split reflow without valid split index (pageIndex=${pageIndex}, splitIndex=${indexOfSplitView}, splitCount=${numberOfSplitViews})`,
          );
        } else {
          const isLastSplitedView = indexOfSplitView === numberOfSplitViews - 1;

          if (indexOfSplitView === 0) {
            // main split view just reset and move on
            lastChildViewModel.render();
          } else if (!isLastSplitedView) {
            // it's not first split view and it's not last, it's something inbetween
            // it should reset pageIndex to the first split view and proceed with the reflow process
            pageIndex = 0;
            const rootView = lastChildViewModel.render();
            if (rootView) {
              const basePage = this.getPageFromView(rootView);
              if (basePage) {
                pageIndex = this.pages.indexOf(basePage);
              }
            }
            continue;
          }
        }
      }

      if (nextPage && !hasBreakPoints) {
        // if no break points check for underflow
        while (
          !workingPage.hasOverflow() &&
          workingPage.hasUnderflow(nextPage) &&
          nextPage.hasChildren()
        ) {
          // if underflow pull paragraphs from next page

          // pull content from next page
          let nextPageFirstChild = nextPage.getFirstBlockView();
          let firstChildViewModel = nextPageFirstChild?.viewModel;
          if (!firstChildViewModel) {
            break;
          }
          firstChildViewModel.removeViews();

          // double check remove for lost views
          if (nextPageFirstChild?.parentNode) {
            nextPageFirstChild.parentNode.removeChild(nextPageFirstChild);
          }

          let rootView = firstChildViewModel.render();
          if (!rootView) {
            break;
          }
          let sectionId = this.Data.sections.getSectionOfBlock(firstChildViewModel.id);
          await workingPage.insertContents(rootView, sectionId, firstChildViewModel);
        }
      }

      // check for overflow or break points
      if (workingPage.hasOverflow() || hasBreakPoints) {
        let overflowRange = workingPage.findOverflow(pageContentContainer);

        if (overflowRange) {
          let children = workingPage.extractContent(
            pageContentContainer,
            overflowRange,
            avoidPaginationProperties,
          );

          logger.info(
            `[PageLayout] reflowPage ${pageIndex} - overflow found`,
            overflowRange,
            children,
          );

          // insert content into next page and create one if it does not exist
          nextPage = this.getNextPage(pageIndex, true);

          if (!nextPage) {
            throw new Error('No next page found during reflow');
          }

          let nextPageFirstChild = nextPage.getFirstBlockView() || undefined;

          for (let c = 0; c < children.length; c++) {
            let child = children[c];
            let sectionId = this.Data.sections.getSectionOfBlock(child.id);
            await nextPage.insertContents(child, sectionId, child.vm, nextPageFirstChild);
          }
        }
      }

      // TODO: if no break points check for underflow
      // else if (workingPage.hasUnderflow()) {
      // }

      // check if page is empty and remove it (but only if it is not the first page)
      if (pageIndex !== 0 && workingPage.isEmpty()) {
        this.removePage(pageIndex);
      } else {
        pageIndex++;
      }
    }
  }

  // /**
  //  * Insert the view of a view model at the specified index
  //  */
  // private insertViewModelView(viewModel: BlockViewModel, index: number) {
  //   if (!viewModel) {
  //     return;
  //   }

  //   //TODO: stop current rendering?

  //   let rootView = viewModel.render();

  //   // TODO: check section??
  //   let sectionId = this.Data.sections.getSectionOfBlock(viewModel.id);

  //   // TODO: compare insert block section with previous and next block?

  //   // TODO: reflow pages?
  // }

  /**
   * Remove the view of a view model from the pages
   */
  private async removeViewModelView(
    viewModel: BlockViewModel,
    blockIndex: number,
    reflowPages: boolean = true,
  ) {
    if (!viewModel) {
      return;
    }

    const view = viewModel.getRootView();
    if (!view) {
      return;
    }

    const workingPage = this.getPageFromView(view);

    if (workingPage) {
      viewModel.removeViews();

      if (reflowPages) {
        await this.reflowPages(this.pages.indexOf(workingPage));
      }
    }
  }

  async scheduleRefreshRender(blockIndex: number) {
    if (this.debug) {
      Logger.trace('[PageLayout] scheduleRefreshRender', blockIndex);
    }

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    // TODO: work on this for sections

    // this.refreshTimeout = setTimeout(async () => {
    //   if (this.renderstatus.isRendering) {
    //     if (this.renderstatus.index < blockIndex) {
    //       if (this.debug) {
    //         Logger.debug(
    //           'Avoided refresh rendering ' + this.renderstatus.index + ' : ' + blockIndex,
    //         );
    //       }
    //       // TODO check
    //       // return this.queue.running?.promise;
    //       return this.renderstatus.isRendering;
    //     }

    //     this.renderstatus.stop();
    //     await this.renderstatus.processing;
    //   } else {
    //     this.renderstatus.stop();
    //   }

    //   return this.refreshRenderFromBlock(blockIndex);
    // }, 50);
  }

  async refreshRenderFromBlock(blockIndex: number) {
    logger.info('[PageLayout] refreshRenderFromBlock', blockIndex);
    // TODO: rework this function

    if (blockIndex < 0) {
      blockIndex = 0;
    }
    this.renderstatus.start(blockIndex);
    let page = this.getPageFromChildIndex(blockIndex) as PageViewModel;
    if (!page) {
      return;
    }
    await this.removeBlocksViews(blockIndex);
    let viewModel;
    for (
      let index = blockIndex;
      index <= this.vm?.children.length - 1 && this.renderstatus.isRendering;
      index++
    ) {
      viewModel = this.vm?.children.getAtIndex(index) as BlockViewModel;
      // this.queue.enqueue(this.layoutViewModelView, viewModel);
      this.renderstatus.processing = this.insertViewModelView(viewModel);
      await this.renderstatus.processing;
    }
    this.renderstatus.stop();
    // this.queue.enqueue(() => this.renderstatus.stop());
    // return this.queue.running?.promise;
  }

  // #endregion
}
